// src/pages/LandingPage.js
// Editorial-diagnostic hero. Serif display heads, restrained CTAs,
// hairline chapter markers, single accent color (gold hairline) used
// sparingly. No pill button decorations, no card-in-card visual noise.

import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TYPE_DEFINITIONS } from '../data/typeDefinitions';
import TypeIcon from '../components/Results/TypeIcon';
import { trackEvent } from '../utils/analytics';

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewedRef = useRef(false);

  const types = Object.values(TYPE_DEFINITIONS);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackEvent('landing_viewed', {
      ref: searchParams.get('ref') || undefined,
      utm_source: searchParams.get('utm_source') || undefined,
      utm_medium: searchParams.get('utm_medium') || undefined,
      utm_campaign: searchParams.get('utm_campaign') || undefined,
    });
  }, [searchParams]);

  const handleGroupShare = async () => {
    const url = `${window.location.origin}/quiz`;
    const text =
      '20 dilemmas. 5 minutes. Find out which of us is the secret Anchor.';
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'What Would You Do?', text, url });
        return;
      } catch {
        /* user cancelled — fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      alert('Link copied — paste it in your group chat.');
    } catch {
      navigate('/quiz');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero — editorial. Small eyebrow, oversized serif head, one CTA
          plus a text link. No pill button pair competing for attention. */}
      <section className="relative">
        <div className="max-w-3xl mx-auto px-6 pt-28 pb-24 md:pt-36 md:pb-32 text-center">
          <motion.p
            className="eyebrow mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            An assessment · 20 dilemmas · 5 minutes
          </motion.p>

          <motion.h1
            className="font-display text-5xl md:text-7xl font-semibold text-neutral-900 leading-[1.05] tracking-tight mb-8"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            What Would You Do?
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-neutral-500 max-w-xl mx-auto leading-relaxed mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            Twenty dilemmas with no easy answers, mapped onto a decision-making
            profile you can share, compare, and use.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <button
              onClick={() => navigate('/quiz')}
              className="btn-primary px-8 py-3.5 text-base"
            >
              Begin the assessment
            </button>
            <button onClick={handleGroupShare} className="btn-link">
              Or send it to your group chat →
            </button>
          </motion.div>

          <motion.p
            className="mt-8 text-xs text-neutral-400 tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Free · No signup required
          </motion.p>
        </div>

        {/* Hairline social-proof band */}
        <div className="border-y border-neutral-200 bg-neutral-50/60">
          <div className="max-w-3xl mx-auto px-6 py-5 text-center">
            <p className="text-sm text-neutral-500">
              <span className="eyebrow mr-2">Most-shared question</span>
              <span className="font-display italic text-neutral-800">
                “Your best friend asks you to lie under oath. Do you?”
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Group + Couple use cases — editorial two-up, no gradient cards */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="eyebrow text-center mb-3">Ways to take it</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-neutral-900 text-center tracking-tight mb-16">
            Alone is useful. Together is where it lands.
          </h2>

          <div className="grid md:grid-cols-2 gap-14 md:gap-16">
            <UseCaseColumn
              chapter="01"
              title="With a friend"
              body="Send it to your group chat. Find out who's secretly the Anchor, and who thinks like the exact opposite of everyone else. Compatibility surprises guaranteed."
              cta="Start with a friend"
              onClick={() => navigate('/quiz?source=friend')}
            />
            <UseCaseColumn
              chapter="02"
              title="With your partner"
              body="See exactly where your values align — and where the next fight is quietly building. The Couple Report shows both profiles side-by-side with the three conversations to have this month."
              cta="Start with your partner"
              onClick={() => navigate('/quiz?source=couple')}
            />
          </div>
        </div>
      </section>

      {/* The six types — restrained list. No colored icon tiles competing. */}
      <section className="py-24 px-6 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-4xl mx-auto">
          <p className="eyebrow text-center mb-3">The framework</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-neutral-900 text-center tracking-tight mb-4">
            Six decision-making types.
          </h2>
          <p className="text-neutral-500 text-center max-w-lg mx-auto mb-14 leading-relaxed">
            Each carries a worldview, a set of blind spots, and a controversial
            opinion you&rsquo;ve probably already had.
          </p>

          <div className="divide-y divide-neutral-200 border-y border-neutral-200 bg-white">
            {types.map(type => (
              <TypeRow key={type.typeName} type={type} />
            ))}
          </div>

          <div className="text-center mt-14">
            <button onClick={() => navigate('/quiz')} className="btn-primary">
              Find out which one you are
            </button>
          </div>
        </div>
      </section>

      {/* How it works — numbered chapters, no round colored badges */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="eyebrow text-center mb-3">How it works</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-neutral-900 text-center tracking-tight mb-16">
            From twenty questions to a diagnostic you can print.
          </h2>

          <div className="grid md:grid-cols-3 gap-10 md:gap-12">
            {[
              {
                step: '01',
                title: 'Face twenty dilemmas',
                desc: 'Scenarios with no easy answer. Your gut reactions reveal more than you think.',
              },
              {
                step: '02',
                title: 'See your profile',
                desc: 'Rarity, controversial opinion, dating red flag, pop-culture matches — a shareable read on how you actually decide.',
              },
              {
                step: '03',
                title: 'Compare with anyone',
                desc: 'Send it to a friend, partner, or coworker and see where your values align and where they clash.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.3, delay: i * 0.08 }}
              >
                <p className="font-display text-3xl text-neutral-300 mb-3 tabular-nums">
                  {item.step}
                </p>
                <h3 className="font-display text-xl font-semibold text-neutral-900 mb-2 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-neutral-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial pull-quote closer */}
      <section className="py-24 px-6 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-2xl mx-auto text-center">
          <p className="eyebrow mb-6">Why bother</p>
          <p className="pull-quote mb-8">
            &ldquo;Most people think they know themselves. Then they face a
            dilemma with no good answer — and their real values show up.&rdquo;
          </p>
          <div className="w-10 h-px bg-accent-500 mx-auto mb-8" />
          <p className="text-neutral-500 leading-relaxed max-w-lg mx-auto">
            The gap between who you think you are and how you actually decide is
            what this assessment finds. In twenty minutes or fewer.
          </p>
        </div>
      </section>

      {/* Final CTA — restrained dark band with typographic hero, no colored button */}
      <section className="py-24 px-6 bg-neutral-900">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-6">
            Begin.
          </h2>
          <p className="text-neutral-400 mb-10 max-w-md mx-auto">
            No signup. Five minutes. Then send it to the one friend you know
            you&rsquo;d disagree with.
          </p>
          <button
            onClick={() => navigate('/quiz')}
            className="inline-flex items-center justify-center bg-white hover:bg-neutral-100 text-neutral-900 text-sm font-semibold px-8 py-3.5 rounded-lg transition-colors"
          >
            Take the assessment
          </button>
        </div>
      </section>
    </div>
  );
}

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

const UseCaseColumn = ({ chapter, title, body, cta, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.35 }}
  >
    <p className="font-display text-lg text-neutral-300 mb-2 tabular-nums">
      {chapter}
    </p>
    <h3 className="font-display text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight mb-4">
      {title}
    </h3>
    <p className="text-neutral-600 leading-relaxed mb-6">{body}</p>
    <button onClick={onClick} className="btn-link">
      {cta} →
    </button>
  </motion.div>
);

const TypeRow = ({ type }) => (
  <div className="flex items-start gap-5 md:gap-6 px-6 py-6 hover:bg-neutral-50/50 transition-colors">
    <div
      className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center mt-0.5"
      style={{ backgroundColor: type.bgColor, color: type.color }}
    >
      <TypeIcon iconKey={type.iconKey} size={20} strokeWidth={1.5} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-3 mb-1.5">
        <p
          className="font-display text-lg font-semibold tracking-tight"
          style={{ color: type.color }}
        >
          {type.typeName}
        </p>
        <p className="text-xs text-neutral-400 truncate">{type.tagline}</p>
      </div>
      <p className="text-sm text-neutral-500 font-display italic leading-snug">
        &ldquo;{type.controversialOpinion}&rdquo;
      </p>
    </div>
  </div>
);

export default LandingPage;
